'use client'

import { getCatalog } from '@/lib/api/catalog/catalog-api'
import {
    applyTemplate,
    getTemplates,
    Template,
    TemplateCategory,
} from '@/lib/api/templates/template-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { MdBolt, MdCheck } from 'react-icons/md'
import { toast } from 'react-toastify'
import { SelectedExam } from './exam-picker'

interface Props {
    /** Modelos de qual profissional. Sem ele, os da organização inteira. */
    professionalId?: number | null
    /** Paciente do pedido — é dele que sai `{{paciente}}` na indicação clínica. */
    clientId: number
    /**
     * Recebe os exames do modelo (já resolvidos em itens do catálogo) e a indicação
     * clínica com as variáveis JÁ trocadas.
     */
    onApply: (
        exams: SelectedExam[],
        template: Template,
        notes: string | null
    ) => void
}

/**
 * Barra de modelos no topo do pedido de exames.
 *
 * É o ponto onde o modelo devolve o trabalho investido: um clique marca os treze
 * exames do check-up de rotina, em vez de treze buscas. Aplicar SOMA ao que já está
 * marcado e não substitui — o médico costuma partir de um modelo e acrescentar o que
 * aquele paciente precisa, e apagar a escolha dele seria destrutivo.
 *
 * Aparece só quando existe modelo: uma barra vazia no topo do pedido seria ruído para
 * quem nunca cadastrou nenhum.
 */
export default function TemplateApplyBar({
    professionalId,
    clientId,
    onApply,
}: Props) {
    const { user, isProfessional } = useAuth()
    const [templates, setTemplates] = useState<Template[]>([])
    const [applyingId, setApplyingId] = useState<number | null>(null)

    const ownerId =
        professionalId ??
        (isProfessional ? (user?.professionalId ?? null) : null)

    useEffect(() => {
        loadTemplates()
    }, [ownerId])

    const loadTemplates = async () => {
        try {
            const response = await getTemplates({
                category: TemplateCategory.EXAM_REQUEST,
                professionalId: ownerId ?? undefined,
            })
            if (response.success) setTemplates(response.result || [])
        } catch (error) {
            // Sem modelo a barra some; o pedido manual continua igual.
        }
    }

    const handleApply = async (template: Template) => {
        setApplyingId(template.id)
        try {
            // O modelo guarda ids; o picker trabalha com o `Catalog` inteiro.
            const loaded = await Promise.all(
                template.items.map(async item => {
                    const response = await getCatalog(item.catalogId)
                    if (!response.success || !response.result) return null
                    return {
                        catalog: response.result,
                        variations: (item.variationCatalogIds || []).map(
                            id => ({ id, name: '' })
                        ),
                    } as SelectedExam
                })
            )

            const exams = loaded.filter(Boolean) as SelectedExam[]

            // A indicação clínica pode ter `{{paciente}}`, e quem resolve é o
            // backend — a mesma função que serve ao laudo e ao impresso. Falhar aqui
            // não impede o modelo: os exames marcados já são o essencial.
            let notes = template.notes ?? null
            try {
                const applied = await applyTemplate(template.id, {
                    clientId,
                    professionalId: professionalId ?? undefined,
                })
                if (applied.success && applied.result) {
                    notes = applied.result.notes
                }
            } catch (error) {
                // Segue com o texto guardado.
            }

            // Item que saiu do catálogo depois que o modelo foi montado: avisa em vez
            // de aplicar em silêncio um pedido menor do que o médico espera.
            const missing = template.items.length - exams.length
            if (missing > 0) {
                toast.warning(
                    missing === 1
                        ? 'Um exame do modelo não está mais no catálogo e ficou de fora.'
                        : `${missing} exames do modelo não estão mais no catálogo e ficaram de fora.`
                )
            }

            onApply(exams, template, notes)

            if (exams.length > 0) {
                toast.success(
                    exams.length === 1
                        ? 'Modelo aplicado: 1 exame marcado'
                        : `Modelo aplicado: ${exams.length} exames marcados`
                )
            }
        } catch (error) {
            toast.error('Erro ao aplicar o modelo')
        } finally {
            setApplyingId(null)
        }
    }

    if (templates.length === 0) return null

    return (
        <div className="border-b border-base-300/60 px-6 py-3">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-base-content/70 flex items-center gap-1">
                    <MdBolt className="text-base text-warning" />
                    Meus modelos:
                </span>
                {templates.map(template => (
                    <button
                        key={template.id}
                        type="button"
                        className="btn btn-sm btn-outline gap-1"
                        onClick={() => handleApply(template)}
                        disabled={applyingId !== null}
                        title={
                            template.notes ||
                            `${template.items.length} exame(s)`
                        }
                    >
                        {applyingId === template.id ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdCheck className="text-base" />
                        )}
                        {template.name}
                        <span className="badge badge-xs badge-ghost">
                            {template.items.length}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    )
}
